import { Box, SelectInput } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  generatePath,
  matchPath,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { ProgramTypeLabels } from 'types';

import RequirePricingAdminUser from '../auth/RequirePricingAdminUser';
import { useHasEnabledNonAnonFlag } from '../FlagGuard/Hook';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';

import { Adders } from './Adders/Adders';
import { PricingHistory } from './History/History';
import { Holdbacks } from './Holdbacks/Holdbacks';
import { CreateITCModifier } from './ITCModifiers/CreateITCModifier';
import { ITCModifiers } from './ITCModifiers/ITCModifiers';
import { Mappings } from './Mappings/Mappings';
import { PriceCaps } from './PriceCaps/PriceCaps';
import { PriceSheets } from './PriceSheets/PriceSheets';
import { SeasonalPlans } from './SeasonalPlans/SeasonalPlans';
import { Modifiers } from './TaxCreditModifiers/Modifiers';
import { Warranties } from './Warranties/Warranties';

const baseRoute = '/admin/pricing/:programType';
const routes = {
  priceSheets: 'price-sheets',
  adders: 'adders/:adderType?',
  modifiers: 'modifiers/:modifierType?',
  holdbacks: 'holdbacks',
  history: 'history',
  itcModifiers: 'itc-modifiers/:modifierType?',
};

const programsWithItcModifiers = new Set([
  ProgramTypeLabels.solar,
  ProgramTypeLabels.newHomes,
  ProgramTypeLabels.consumerSolar,
  ProgramTypeLabels.geothermal,
]);

export function Pricing() {
  const { checkFlags: showPricingHistory } = useHasEnabledNonAnonFlag(['showPricingHistory']);
  const isPricingHistoryEnabled = showPricingHistory();
  const { seasonalPricing: isSeasonalPricingEnabled = false } = useFlags();
  const programTypesOptions = Object.entries(ProgramTypeLabels).map(([key, value]) => ({
    label: value,
    key,
    value,
  }));
  const [programType, setProgramType] = useState(programTypesOptions[0]);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    const { programType: urlProgramType = '' } = params;
    if (programType.key !== urlProgramType) {
      const lastProgramType =
        programTypesOptions.find((option) => option.key === urlProgramType) || programTypesOptions[0];
      setProgramType(lastProgramType);
    }
  }, [params, programType, programTypesOptions]);

  const handleProgramTypeChange = (event: any) => {
    setProgramType(event.target.value);
    const pathPattern =
      Object.values(routes).find((pattern: string) => matchPath(`${baseRoute}/${pattern}`, location.pathname)) || '';
    const path = generatePath(`${baseRoute}/${pathPattern}`, { ...params, programType: event.target.value.key });
    navigate(path);
  };
  return (
    <>
      <Helmet>
        <title>Pricing</title>
      </Helmet>
      <MainContainer maxWidth="100">
        <Box maxWidth="2xl">
          <SelectInput
            id="required"
            label="Program Type"
            value={programType}
            onChange={handleProgramTypeChange}
            options={programTypesOptions}
            menuPortalTarget={document.body}
          />
        </Box>
        <PageHeader title={`${programType.label} Pricing`}></PageHeader>
        <Box direction="row" borderWidth="0 0 xs 0" borderColor="separator">
          <NavLink to={''} style={{ textDecoration: 'none', fontWeight: '500' }} end>
            {({ isActive }) => (
              <Box
                as="span"
                padding="md"
                className={isActive ? 'tab-active' : ''}
                hover={{ color: 'primary' }}
                style={{ transitionDuration: '0s' }}
              >
                Mappings
              </Box>
            )}
          </NavLink>
          <NavLink to={`price-sheets`} style={{ textDecoration: 'none', fontWeight: '500' }} end>
            {({ isActive }) => (
              <Box
                as="span"
                padding="md"
                className={isActive ? 'tab-active' : ''}
                hover={{ color: 'primary' }}
                style={{ transitionDuration: '0s' }}
              >
                Price Sheets
              </Box>
            )}
          </NavLink>
          {programType.label === ProgramTypeLabels.hvac && (
            <NavLink to={`warranties`} style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s' }}
                >
                  Warranties
                </Box>
              )}
            </NavLink>
          )}

          {![
            ProgramTypeLabels.hvac,
            ProgramTypeLabels.energyBackup,
            ProgramTypeLabels.waterHeater,
            ProgramTypeLabels.generator,
            ProgramTypeLabels.geothermal,
          ].includes(programType.label) && (
            <NavLink to={`adders`} style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s' }}
                >
                  Construction Adders
                </Box>
              )}
            </NavLink>
          )}
          {[ProgramTypeLabels.energyBackup].includes(programType.label) && (
            <NavLink to={`price-caps`} style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s' }}
                >
                  Price & Term Caps
                </Box>
              )}
            </NavLink>
          )}
          {[ProgramTypeLabels.solar, ProgramTypeLabels.newHomes, ProgramTypeLabels.consumerSolar].includes(
            programType.label,
          ) && (
            <NavLink to={`modifiers`} style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s' }}
                >
                  Modifiers
                </Box>
              )}
            </NavLink>
          )}
          {[ProgramTypeLabels.solar, ProgramTypeLabels.newHomes].includes(programType.label) && (
            <NavLink to={`holdbacks`} style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s' }}
                >
                  Holdbacks
                </Box>
              )}
            </NavLink>
          )}
          {programsWithItcModifiers.has(programType.label) && (
            <NavLink to={`itc-modifiers`} style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s' }}
                >
                  ITC Modifiers
                </Box>
              )}
            </NavLink>
          )}
          {programType.label === ProgramTypeLabels.solar && isSeasonalPricingEnabled && (
            // Every seasonal-plan route requires pricingAdmin, so the tab is hidden rather than left to 404.
            <RequirePricingAdminUser>
              <NavLink to={`seasonal-plans`} style={{ textDecoration: 'none', fontWeight: '500' }}>
                {({ isActive }) => (
                  <Box
                    as="span"
                    padding="md"
                    className={isActive ? 'tab-active' : ''}
                    hover={{ color: 'primary' }}
                    style={{ transitionDuration: '0s' }}
                  >
                    Seasonal Plans
                  </Box>
                )}
              </NavLink>
            </RequirePricingAdminUser>
          )}
          {isPricingHistoryEnabled && (
            <NavLink to={'history'} style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s' }}
                >
                  History
                </Box>
              )}
            </NavLink>
          )}
        </Box>
        <Routes>
          <Route path="/" element={<Mappings />} />
          <Route path="/price-sheets/" element={<PriceSheets />} />
          <Route path="/adders/:adderType?" element={<Adders />} />
          <Route path="/price-caps/:priceCapType?" element={<PriceCaps />} />
          {[ProgramTypeLabels.solar, ProgramTypeLabels.newHomes, ProgramTypeLabels.consumerSolar].includes(
            programType.label,
          ) && <Route path="/modifiers/:modifierType?/*" element={<Modifiers />} />}
          {[ProgramTypeLabels.solar, ProgramTypeLabels.newHomes].includes(programType.label) && (
            <Route path="/holdbacks" element={<Holdbacks />} />
          )}
          {programsWithItcModifiers.has(programType.label) && (
            <>
              <Route path="/itc-modifiers/:modifierType?/create" element={<CreateITCModifier />} />
              <Route path="/itc-modifiers/:modifierType?/*" element={<ITCModifiers />} />
            </>
          )}
          {programType.label === ProgramTypeLabels.solar && isSeasonalPricingEnabled && (
            <Route path="/seasonal-plans/*" element={<SeasonalPlans programType={programType.key} />} />
          )}
          {programType.label === ProgramTypeLabels.hvac && (
            <Route path="/warranties/:warrantyType?" element={<Warranties />} />
          )}
          {isPricingHistoryEnabled && <Route path="/history" element={<PricingHistory />} />}
        </Routes>
      </MainContainer>
    </>
  );
}
